import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, BookOpen, Cpu, Scale, Building2 } from "lucide-react";
import SEO from "@/components/SEO";

const PRINCIPIOS = [
  {
    n: "01",
    title: "Cumplimiento operativo, no decorativo",
    body: "No vendemos sellos ni certificados ficticios. Cada hallazgo cita su artículo y literal de la Ley 21.719. Cada documento se redacta con base en la guía oficial del Min. Segpres.",
  },
  {
    n: "02",
    title: "Hecho en Chile, para Chile",
    body: "PYME chilena, e-commerce local, ONG y consultorías que necesitan cumplir sin pagar $250.000+ por un abogado de marca. Precio único $14.990 CLP · pago único · acceso permanente.",
  },
  {
    n: "03",
    title: "Transparencia técnica",
    body: "Mostramos qué señales detectamos, cómo las puntuamos y qué obligación legal incumplen. Publicamos la metodología y permitimos a tu equipo legal auditar el output.",
  },
];

const FUENTES = [
  { label: "Ley 21.719 — BCN", href: "https://www.bcn.cl/leychile/navegar?idNorma=141599" },
  { label: "Guía Práctica Min. Segpres", href: "https://wikiguias.digital.gob.cl/datos-personales/guia-practica-implementacion-nueva-ley-datos-personales" },
  { label: "Cláusulas Modelo Min. Economía (RAEX 2025-03731)", href: "https://www.economia.gob.cl/2025/12/10/raex202503731-aprueba-las-clausulas-contractuales-modelo-para-transferencias-internacionales-que-indica.htm" },
  { label: "Decreto 7/2023 medidas de seguridad", href: "https://www.bcn.cl/leychile/navegar?idNorma=1190099" },
];

export default function SobreNosotros() {
  return (
    <>
      <SEO
        title="Sobre nosotros"
        description="Ley de Datos Chile es la herramienta operativa de cumplimiento Ley 21.719 hecha para PYMEs chilenas. Sin abogados de marca, sin plantillas genéricas."
        path="/sobre-nosotros"
      />

      {/* HERO dark */}
      <section className="relative bg-ink-950 text-white overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 lg:pt-28 lg:pb-24">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-7">
            <span className="inline-block w-6 h-px bg-blue-400/60" />
            Sobre Ley de Datos Chile
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight max-w-4xl">
            Hicimos lo que un <em className="italic text-blue-300">abogado top</em> haría por tu sitio, a precio de PYME.
          </h1>
          <p className="mt-8 text-lg text-slate-300 leading-relaxed max-w-3xl">
            Somos un equipo de ingenieros + abogados especialistas en protección de datos. Construimos
            <strong className="text-white"> Ley de Datos Chile</strong> porque la nueva Ley 21.719 entra en vigencia plena el 1 de diciembre de 2026
            y la mayoría de las PYMEs chilenas no tienen presupuesto para pagar $250.000+ por una auditoría legal manual.
          </p>
        </div>
      </section>

      {/* Quiénes somos */}
      <section className="px-6 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
              Quiénes somos
            </p>
            <h2 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight">
              Ingenieros + abogados, <em className="italic text-blue-700">no</em> marketing.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-700 text-base sm:text-lg leading-relaxed">
            <p>
              <strong className="text-slate-900">Ley de Datos Chile</strong> es una plataforma chilena que combina escaneo técnico automatizado, modelos de IA entrenados con la
              normativa chilena (Ley 21.719, Decreto N° 7/2023, RAEX 2025-03731) y revisión legal humana.
            </p>
            <p>
              Atendemos PYMEs, e-commerce, ONG, agencias digitales, clínicas, consultorías y equipos legales internos que necesitan demostrar cumplimiento sin transformar el día a día.
            </p>
            <p className="text-slate-500 text-sm pt-3 border-t border-slate-200">
              Razón social: <span className="text-slate-700 font-medium">Ley de Datos Chile</span> · Santiago, Chile.
            </p>
          </div>
        </div>
      </section>

      {/* Principios numerados estilo Reaktor */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-20 sm:py-24">
          <div className="mb-14 max-w-2xl">
            <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
              Cómo trabajamos
            </p>
            <h2 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight">
              Tres principios que no negociamos.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {PRINCIPIOS.map((p) => (
              <article
                key={p.n}
                data-testid={`principio-${p.n}`}
                className="bg-white border border-slate-200 rounded-2xl p-7 hover:shadow-lg transition-shadow"
              >
                <span className="font-mono text-xs text-blue-700 tracking-wider">{p.n}</span>
                <h3 className="mt-3 font-display text-2xl text-slate-900 tracking-tight leading-snug">
                  {p.title}
                </h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Stack técnico + fuentes oficiales */}
      <section className="px-6 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
              Stack técnico
            </p>
            <h2 className="font-display text-4xl text-slate-900 leading-tight tracking-tight">
              Construido sobre tecnología <em className="italic text-blue-700">verificable</em>.
            </h2>
            <ul className="mt-8 space-y-5">
              <li className="flex items-start gap-3">
                <Cpu className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-900">Claude Sonnet 4.5 (Anthropic)</p>
                  <p className="text-sm text-slate-600 mt-0.5">Modelo de lenguaje para generación legal + 2da pasada de auditoría automática.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-900">Escáner técnico propio</p>
                  <p className="text-sm text-slate-600 mt-0.5">HTTPS, banner cookies, +22 trackers, formularios, decisiones automatizadas, ARCO, DPO, RAT.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Scale className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-900">Motor de multas determinístico</p>
                  <p className="text-sm text-slate-600 mt-0.5">Arts. 34 bis/ter/quáter. UTM editable. PYME 1ra vez vs no-PYME / reincidente. Sin LLM.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <BookOpen className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-slate-900">7 documentos legales</p>
                  <p className="text-sm text-slate-600 mt-0.5">Política Art. 14 ter · Cláusula consentimiento · DPA · Catálogo Datos (RAT) · CCM · 2 más.</p>
                </div>
              </li>
            </ul>
          </div>
          <div className="bg-ink-950 text-white rounded-2xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
            <div className="relative">
              <p className="font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-4">
                Fuentes oficiales que citamos
              </p>
              <h3 className="font-display text-3xl text-white leading-tight tracking-tight">
                Sin inventar, sin reescribir.
              </h3>
              <p className="mt-5 text-sm text-slate-300 leading-relaxed">
                Cada documento generado cita su base legal y enlaza a la fuente oficial publicada por el gobierno chileno.
              </p>
              <ul className="mt-7 space-y-3">
                {FUENTES.map((f) => (
                  <li key={f.href}>
                    <a
                      href={f.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-start gap-2 text-sm text-slate-200 hover:text-white transition-colors"
                    >
                      <span className="text-blue-300 font-mono text-[10px] mt-1 tracking-wider">→</span>
                      <span className="underline-offset-4 group-hover:underline">{f.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA bottom */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-20 sm:py-24 text-center">
          <Building2 className="w-10 h-10 text-blue-700 mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight max-w-2xl mx-auto">
            ¿Quieres ver cómo le va a <em className="italic text-blue-700">tu sitio</em>?
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-xl mx-auto">
            30 segundos. Sin registro. 3 análisis gratis.
          </p>
          <div className="mt-9 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/"
              data-testid="sobre-cta-escaner"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors"
            >
              Escanear mi sitio gratis <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contacto"
              data-testid="sobre-cta-contacto"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-300 hover:bg-white text-slate-700 font-semibold transition-colors"
            >
              Hablar con el equipo
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
